import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { useBranding } from "./BrandingContext";
import uiPreferencesApi from "../api/uiPreferencesApi";
import {
    KLUCZ_MOTYWU,
    KanalMotywuKonta,
    MOTYW_DOMYSLNY,
    czyMotyw,
    czytajWyborMotywu,
    czytajWlascicielaMotywu,
    motywEkranuBezKonta,
    motywStartowy,
    motywTegoKonta,
    rozstrzygnijMotywKonta,
    wlascicielLogowania,
    wlascicielOczekujacy,
    wlascicielZgodny,
    zapiszWlascicielaMotywu,
    zapiszWyborMotywu,
} from "../utils/motywAplikacji";

/**
 * Personalizacja wyglądu (per przeglądarka, localStorage):
 *  - accent  → kolor dominujący aplikacji (sidebar / header / hero / akcenty + ramki). NIE dotyka #main-content.
 *  - font    → rodzina czcionki interfejsu.
 * Aplikuje się przez atrybuty html[data-accent] / html[data-font]; resztę robi styles/accent.css.
 *
 * [G2 2026-08-31] WYBÓR JEST TERAZ ZAPISYWANY NA KONCIE, nie tylko w przeglądarce.
 * Było źle: kolor i czcionka leżały WYŁĄCZNIE w localStorage, więc każde nowe urządzenie, każda
 * inna przeglądarka i każde wyczyszczenie danych witryny resetowały wygląd — a użytkownik nie miał
 * jak się domyślić dlaczego. Teraz źródłem prawdy jest konto (GET/PUT /api/user-preferences),
 * a localStorage zostaje jako PAMIĘĆ PODRĘCZNA na czas startu (żeby przy pierwszym renderze nie
 * mignął domyślny wygląd) i jako tryb offline — gdy serwer nie odpowie, wszystko działa jak dotąd.
 *
 * Co jedzie na serwer: `accent` TYLKO wtedy, gdy człowiek sam wybrał kolor (inaczej jego interfejs
 * ma podążać za paletą spółki na każdym urządzeniu), oraz `font`, gdy inna niż domyślna.
 * Brak klucza na serwerze znaczy wprost „nie mam własnego wyboru", a nie „jeszcze nie wiem".
 * Działa równolegle z dostępnością — nie koliduje.
 *
 * [polecenie właściciela 2026-10-03] MOTYW (jasny/ciemny) JEDZIE TYM SAMYM ZAPISEM. „ustaw, że kolor domyślny
 * aplikacji to biały, nie ciemny, user to potem sobie zmieni”. `theme` trafia na serwer WYŁĄCZNIE po kliknięciu
 * (brak klucza = domyślny jasny). Ten kontekst jest JEDYNYM pisarzem preferencji, bo PUT podmienia CAŁY obiekt:
 * osobny zapis motywu kasowałby kolor i czcionkę (albo odwrotnie). ThemeProvider dostaje wybór kanałem
 * `KanalMotywuKonta` (utils/motywAplikacji.js) — nie ma własnego żądania, więc wejście na stronę nie kosztuje
 * nic więcej (zapadka H1). Tu też żyje znacznik okna „Wygląd aplikacji” (raz na konto, z lustrem w przeglądarce).
 *
 * [BRANDING PER FIRMA] Kolejność ważności koloru:
 *   1. WŁASNY wybór pracownika (accentByUser w localStorage) — zawsze wygrywa,
 *   2. domyślna paleta SPÓŁKI pracownika (Company.DefaultAccent, jedzie w /api/branding),
 *   3. domyślna paleta aplikacji ("granat").
 * Dopóki człowiek sam nie kliknie koloru, jego interfejs podąża za ustawieniem spółki —
 * także wtedy, gdy administrator zmieni je po czasie.
 */
const PersonalizationContext = createContext();
export const usePersonalization = () => useContext(PersonalizationContext);

const STORAGE_KEY = "app-personalization";

// Palety akcentow — os kola barw od wlasciciela (2026-08-28) + rodzina zlota marki.
// Gradienty hero liczone z tych barw PASTELOWO w styles/accent.css (generator w komentarzu tamze).
// "granat" = domyslny. Uwaga historyczna: #c6a45b rozni sie od #c6a45c o jeden stopien skladowej
// niebieskiej — to ten sam kolor, celowo jedna pozycja.
export const ACCENTS = [
    { id: "granat", label: "Granat", swatch: "#3a56c5" },
    { id: "blekit", label: "Błękit", swatch: "#0e7fc9" },
    { id: "turkus", label: "Turkus", swatch: "#0891b2" },
    { id: "morski", label: "Morski", swatch: "#0fb5a5" },
    { id: "szmaragd", label: "Szmaragd", swatch: "#10b981" },
    { id: "zielen", label: "Zieleń", swatch: "#3f9142" },
    { id: "limonka", label: "Limonka", swatch: "#7ba428" },
    { id: "cytryna", label: "Cytryna", swatch: "#c9a227" },
    { id: "bursztyn", label: "Bursztyn", swatch: "#e08a16" },
    { id: "pomarancz", label: "Pomarańcz", swatch: "#e2611c" },
    { id: "czerwien", label: "Czerwień", swatch: "#e23b3b" },
    { id: "roza", label: "Róż", swatch: "#e0467e" },
    { id: "magenta", label: "Magenta", swatch: "#c02790" },
    { id: "purpura", label: "Purpura", swatch: "#9333ea" },
    { id: "fiolet", label: "Fiolet", swatch: "#8b5cf6" },
    { id: "indygo", label: "Indygo", swatch: "#6366f1" },
    { id: "zloto", label: "Złoto", swatch: "#c6a45c" },
    { id: "szampan", label: "Szampan", swatch: "#d9c08a" },
    { id: "miedz", label: "Miedź", swatch: "#b87333" },
    { id: "oliwka", label: "Oliwka", swatch: "#8f8f4a" },
    { id: "szary", label: "Szary", swatch: "#6b7280" },
    { id: "czarny", label: "Czarny", swatch: "#1a1a1a" },
    // [poz. 28, brief 4.09] Dwie palety dołożone przez właściciela. Kolor tekstu na
    // wypełnieniu NIE jest tu wpisany z ręki — wynika z jasności tła (patrz accent.css
    // i zapadka paletaKontrastAkcentu.j28): biel → atrament ciemny, granat → biały.
    { id: "bialy", label: "Biały", swatch: "#ffffff" },
    { id: "noc", label: "Granat głęboki", swatch: "#131e3c" },
];
const ACCENT_IDS = ACCENTS.map((a) => a.id);
const DEFAULT_ACCENT = "granat";

// Czcionki. "default" = obecna (Montserrat). Pozostałe ładowane w styles/accent.css (@import).
export const FONTS = [
    { id: "default",  label: "Domyślna (Montserrat)", preview: "Montserrat, sans-serif" },
    { id: "inter",    label: "Inter (nowoczesna)",    preview: "Inter, sans-serif" },
    { id: "lora",     label: "Lora (szeryfowa)",      preview: "Lora, Georgia, serif" },
    { id: "atkinson", label: "Czytelna (Atkinson)",   preview: "'Atkinson Hyperlegible', sans-serif" },
];
const FONT_IDS = FONTS.map((f) => f.id);
const DEFAULT_FONT = "default";

/* ── [2026-10-03] OKNO „WYGLĄD APLIKACJI” — RAZ NA KONTO ─────────────────────────────────────────────────
   Znacznik leży na KONCIE (`oknoWygladu` w UiPreferencesJson, zapis: POST /api/user-preferences/okno-wygladu),
   bo właściciel już raz reklamował „baner co logowanie”, gdy pamięć była tylko w przeglądarce. Lustro w
   localStorage (per konto) chroni przed ponownym oknem, gdy zapis na serwer nie doszedł.
   ⚠️ Ta sama wersja stoi w UserPreferencesController.WersjaOknaWygladu — zapadka 1003 porównuje obie.
   Podbicie wersji = okno pokaże się każdemu jeszcze raz (np. przy nowym motywie). */
export const WERSJA_OKNA_WYGLADU = "2026-10-03";
const KLUCZ_OKNA_WYGLADU = "oknoWygladu";
const kluczLustraOkna = (idKonta) => `okno-wygladu:${idKonta}`;
const czytajLustroOkna = (idKonta) => {
    try { return window.localStorage.getItem(kluczLustraOkna(idKonta)) === WERSJA_OKNA_WYGLADU; } catch { return false; }
};
const zapiszLustroOkna = (idKonta) => {
    try { window.localStorage.setItem(kluczLustraOkna(idKonta), WERSJA_OKNA_WYGLADU); } catch { /* quota */ }
};

/* [poprawka po przeglądzie 2026-10-04] Nieudany odczyt preferencji (typowo: restart usługi przy wdrożeniu, 502)
   NIE odblokowuje zapisu — ponawiamy odczyt z odczekaniem. Do skutku zapisów na konto nie ma, a okno wyglądu czeka. */
export const PONOWIENIA_ODCZYTU_PREFERENCJI_MS = [15000, 60000];
/* [domknięcie po krytyku 2026-10-04] …a po wyczerpaniu tej listy NIE poddajemy się do końca sesji. BYŁO ŹLE: po trzech
   nieudanych odczytach (0 s, 15 s, 75 s) zapis na konto był zablokowany do końca sesji — okno wyglądu się nie pokazywało,
   a kolor i czcionka wybrane w tej sesji przepadały przy następnym wejściu. Teraz: dalej co 5 minut oraz od razu, gdy
   przeglądarka odzyska sieć (`online`) albo karta wróci na wierzch (`visibilitychange`). Obiekt, nie liczba — zapadka
   skraca czas bez czekania 5 minut. */
export const PONOWIENIE_CYKLICZNE_ODCZYTU = { ms: 5 * 60 * 1000 };

/** Zestaw wysyłany na serwer. JEDNA funkcja dla zapisu i dla podpisu tego, co już leży na koncie —
    kolejność kluczy decyduje o porównaniu, a echo PUT po każdym wczytaniu to zbędny zapis do bazy. */
const zestawKonta = ({ accent, accentByUser, font, motyw }) => {
    const zestaw = {};
    if (accentByUser && accent) zestaw.accent = accent;
    if (font && font !== DEFAULT_FONT) zestaw.font = font;
    if (czyMotyw(motyw)) zestaw.theme = motyw;
    return zestaw;
};

const readInitial = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const p = JSON.parse(raw);
            const accent = ACCENT_IDS.includes(p.accent) ? p.accent : DEFAULT_ACCENT;
            return {
                accent,
                font: FONT_IDS.includes(p.font) ? p.font : DEFAULT_FONT,
                // Migracja starych wpisów bez flagi: kolor różny od domyślnego mógł powstać
                // tylko z kliknięcia człowieka — traktujemy go jako wybór własny.
                accentByUser: typeof p.accentByUser === "boolean" ? p.accentByUser : accent !== DEFAULT_ACCENT,
            };
        }
    } catch { /* brak/zły wpis */ }
    return { accent: DEFAULT_ACCENT, font: DEFAULT_FONT, accentByUser: false };
};

export const PersonalizationProvider = ({ children }) => {
    const initial = readInitial();
    const [accent, setAccentState] = useState(initial.accent);
    const [accentByUser, setAccentByUser] = useState(initial.accentByUser);
    const [font, setFontState] = useState(initial.font);
    // [2026-10-03] Jawny wybór motywu ("light" | "dark") albo null = nikt nie wybierał (obowiązuje jasny).
    // [domknięcie po krytyku 2026-10-04] Start tą samą regułą co skrypt #motyw-startowy: bez sesji w przeglądarce
    // (ekran logowania po wylogowaniu) wybór konta NIE jest pokazywany — patrz motywStartowy w utils/motywAplikacji.js.
    const [motyw, setMotyw] = useState(() => motywStartowy());
    // Czy preferencje konta naprawdę przyszły z serwera (STAN, nie ref — okno wyglądu musi dostać sygnał).
    const [preferencjeWczytane, setPreferencjeWczytane] = useState(false);
    const [oknoNaKoncie, setOknoNaKoncie] = useState(false);
    const [oknoLokalnie, setOknoLokalnie] = useState(false);

    const { user, podgladAktywny, podgladWidok } = useAuth() || {};
    const wPodgladzie = !!podgladAktywny;
    const podgladRef = useRef(wPodgladzie);
    podgladRef.current = wPodgladzie;
    /* [domknięcie po krytyku 2026-10-04] WYMUSZONA ZMIANA HASŁA (konto lokalne: hasło tymczasowe albo po 45 dniach).
       Serwer odrzuca wtedy wszystko poza zmianą hasła (403 „haslo_do_zmiany”), więc odczyt preferencji tylko zjadał
       ponowienia. BYŁO ŹLE: zmiana hasła trwająca ponad 75 s wyczerpywała je, a po zmianie (to samo konto, ten sam
       `user.id`) nic nie czytało preferencji ponownie — do końca sesji bez zapisu na konto i bez okna wyglądu.
       Teraz czekamy, aż flaga zejdzie (odświeżenie sesji po zmianie), i wtedy czytamy. Wzór: NotificationContext. */
    const hasloDoZmiany = user?.hasloDoZmiany === true;
    // Liczba nieudanych odczytów preferencji dla bieżącego konta (ponowienia z odczekaniem).
    const [probaOdczytu, setProbaOdczytu] = useState(0);
    const probyOdczytu = useRef({ klucz: null, ile: 0 });
    // Czy odczyt jest w drodze (ponowienie „na żądanie” — sieć wróciła, karta na wierzchu — nie dubluje żądania).
    const odczytWLocie = useRef(false);
    // Konto z poprzedniego przebiegu efektu odczytu: odróżnia WYLOGOWANIE (konto → brak) i WEJŚCIE (brak → konto).
    const poprzednieKonto = useRef(null);
    const { companyAccents } = useBranding();

    /* [BC-10, przebudowa 2026-09-19] „jego kolory ustawione" — NAKŁADKA WYGLĄDU.
       Gdy administrator ogląda czyjś widok, dokument dostaje akcent i czcionkę TAMTEJ osoby
       (`UiPreferencesJson` policzone przez serwer). Dwie rzeczy są tu celowe i obie ratują dane:
       (1) nakładka NIE zmienia stanu `accent`/`font`, tylko to, co trafia na `<html>`; (2) zapis na
       konto jest w tym czasie wstrzymany — inaczej pół minuty oglądania cudzego widoku nadpisałoby
       administratorowi jego własny wybór koloru. */
    const wygladPodgladu = podgladAktywny ? (podgladWidok?.wyglad || {}) : null;
    const accentEfektywny = (wygladPodgladu && ACCENT_IDS.includes(wygladPodgladu.accent))
        ? wygladPodgladu.accent
        : (wygladPodgladu ? DEFAULT_ACCENT : accent);
    const fontEfektywny = (wygladPodgladu && FONT_IDS.includes(wygladPodgladu.font))
        ? wygladPodgladu.font
        : (wygladPodgladu ? DEFAULT_FONT : font);

    /* Dopóki nie wiemy, co stoi na koncie, NIE zapisujemy niczego na serwer — inaczej wartości
       z pamięci podręcznej przeglądarki nadpisałyby świeży wybór zrobiony na innym urządzeniu.
       [poprawka po przeglądzie 2026-10-04] „Wiemy” = UDANY odczyt dla TEGO konta. BYŁO ŹLE: blok finally ustawiał
       flagę także po błędzie odczytu, a zmiana konta (wejście/wyjście z podglądu) jej nie zerowała — pierwsza zmiana
       stanu wysyłała wtedy zestaw z pamięci przeglądarki i PUT (podmiana CAŁEGO obiektu) kasował wybór z innego
       urządzenia albo wysyłał na konto motyw innej osoby. */
    const wczytanoZKonta = useRef(false);
    const ostatniZapis = useRef(null);

    /* [2026-10-03] KOLEJKA ZAPISÓW. Zapis zestawu (PUT, z opóźnieniem 600 ms) i znacznik okna (POST) dotykają
       tego samego wiersza. Dwa żądania w locie naraz = ostatni wygrywa i jedno z nich ginie. Z jednej karty idą
       więc gęsiego; serwer i tak przenosi znacznik przy każdym PUT, kolejka zamyka resztę okna wyścigu. */
    const kolejkaZapisow = useRef(Promise.resolve());
    const wKolejce = useCallback((zadanie) => {
        const wynik = kolejkaZapisow.current.then(zadanie);
        kolejkaZapisow.current = wynik.catch(() => {});
        return wynik;
    }, []);

    const wyslijZnacznikOkna = useCallback(() => wKolejce(() => uiPreferencesApi.oznaczOknoWygladu())
        .then((res) => {
            if (res?.data?.preferences?.[KLUCZ_OKNA_WYGLADU] === WERSJA_OKNA_WYGLADU) setOknoNaKoncie(true);
        })
        .catch(() => { /* lustro w przeglądarce zostaje; przy następnym wejściu dosyłamy jeszcze raz */ }), [wKolejce]);

    useEffect(() => {
        /* Każda zmiana konta albo trybu podglądu = nie wiemy, co stoi na koncie. Zerujemy WSZYSTKO, co odblokowuje
           zapis (wejście w podgląd podmienia `user` na osobę oglądaną, wyjście — z powrotem). */
        wczytanoZKonta.current = false;
        ostatniZapis.current = null;
        setPreferencjeWczytane(false);
        setOknoNaKoncie(false);
        const klucz = `${user?.id ?? ""}|${wPodgladzie}|${hasloDoZmiany}`;
        if (probyOdczytu.current.klucz !== klucz) probyOdczytu.current = { klucz, ile: 0 };
        const poprzednie = poprzednieKonto.current;
        poprzednieKonto.current = user?.id ?? null;
        /* [domknięcie po krytyku 2026-10-04] WYLOGOWANIE: ekran logowania pokazuje domyślny jasny (albo kliknięty przed chwilą
           na tym ekranie), a nie motyw osoby, która właśnie wyszła. Jej wybór zostaje w przeglądarce na jej następne logowanie. */
        if (!user?.id && poprzednie != null) {
            setMotyw(motywEkranuBezKonta({ lokalny: czytajWyborMotywu(), wlasciciel: czytajWlascicielaMotywu() }));
        }
        /* [poprawka po przeglądzie 2026-10-04] W PODGLĄDZIE cudzego konta nie czytamy preferencji wcale: serwer oddałby
           dane OGLĄDANEJ osoby (BF-1), a ich kolory i tak przychodzą nakładką (`podgladWidok.wyglad`). BYŁO ŹLE: odczyt
           zapisywał jej motyw i znacznik okna w przeglądarce administratora jako JEGO wybór, a po wyjściu z podglądu
           automatyczny PUT/POST niósł je na konto administratora. Na ekranie zostaje motyw administratora. */
        if (!user?.id || wPodgladzie) {
            setOknoLokalnie(false);
            return undefined;
        }
        const idKonta = user.id;
        /* [domknięcie po krytyku 2026-10-04] WEJŚCIE NA KONTO (logowanie): jego własny wybór z tej przeglądarki od razu,
           bez czekania na serwer — ekran nie miga jasny → ciemny. Tylko gdy na ekranie stoi domyślny; wybór innej osoby
           z tego komputera nie jest pokazywany (rozstrzygnie odczyt konta niżej, jak dotąd). */
        if (poprzednie == null) {
            const wlasny = motywTegoKonta({ lokalny: czytajWyborMotywu(), wlasciciel: czytajWlascicielaMotywu(), idKonta });
            if (wlasny) setMotyw((obecny) => obecny ?? wlasny);
        }
        setOknoLokalnie(czytajLustroOkna(idKonta));
        // Wymuszona zmiana hasła: serwer i tak odmówi — czytamy dopiero, gdy flaga zejdzie (zależność efektu).
        if (hasloDoZmiany) return undefined;
        let anulowane = false;
        let ponowienie = null;
        (async () => {
            let res;
            odczytWLocie.current = true;
            try {
                res = await uiPreferencesApi.pobierz();
            } catch {
                /* Serwer poza zasięgiem — zostajemy przy tym, co jest w przeglądarce, ale NIC nie zapisujemy na konto
                   i okna wyglądu nie pokazujemy (preferencje niewczytane). Ponawiamy odczyt z odczekaniem: najpierw
                   wg listy, potem cyklicznie (i od razu po powrocie sieci albo karty — efekt niżej). */
                if (anulowane) return;
                odczytWLocie.current = false;
                const nr = probyOdczytu.current.ile;
                probyOdczytu.current.ile = nr + 1;
                const zwloka = nr < PONOWIENIA_ODCZYTU_PREFERENCJI_MS.length
                    ? PONOWIENIA_ODCZYTU_PREFERENCJI_MS[nr]
                    : PONOWIENIE_CYKLICZNE_ODCZYTU.ms;
                ponowienie = setTimeout(() => setProbaOdczytu((x) => x + 1), zwloka);
                return;
            }
            if (anulowane) return;
            odczytWLocie.current = false;
            const dane = res?.data || {};
            /* Odpowiedź o KIMŚ INNYM niż to konto: F5 w trybie podglądu (powłoka jeszcze nie wie, że podgląd trwa,
               a serwer już odpowiada danymi oglądanej osoby) albo wyścig przy wyjściu z podglądu. Nie ruszamy niczego —
               poprawny odczyt przyjdzie, gdy powłoka pozna swój stan. */
            if (dane.podglad === true) return;
            if (dane.konto != null && String(dane.konto) !== String(idKonta)) return;
            const p = dane.preferences || {};
            if (ACCENT_IDS.includes(p.accent)) {
                setAccentState(p.accent);
                setAccentByUser(true);
            } else {
                // Brak koloru na koncie = brak własnego wyboru → wracamy pod paletę spółki.
                setAccentByUser(false);
            }
            setFontState(FONT_IDS.includes(p.font) ? p.font : DEFAULT_FONT);

            /* MOTYW — rozstrzyga `rozstrzygnijMotywKonta` (utils/motywAplikacji.js):
               świeże kliknięcie z ekranu logowania > motyw konta > niepotwierdzone kliknięcie tego konta > brak (jasny).
               Wybór „zgodny z kontem” przy koncie BEZ motywu = zdjęty na innym urządzeniu → jasny, nie wraca na konto.
               Wybór innej osoby z tego samego komputera i stare kliknięcie z logowania → jasny i sprzątnięte. */
            const motywNaKoncie = czyMotyw(p.theme) ? p.theme : null;
            const { motyw: wybrany } = rozstrzygnijMotywKonta({
                motywKonta: motywNaKoncie,
                lokalny: czytajWyborMotywu(),
                wlasciciel: czytajWlascicielaMotywu(),
                idKonta,
            });
            if (!wybrany) zapiszWyborMotywu(null);
            else if (wybrany === motywNaKoncie) zapiszWyborMotywu(wybrany, wlascicielZgodny(idKonta));
            else zapiszWyborMotywu(wybrany, wlascicielOczekujacy(idKonta)); // jedzie na konto zapisem niżej
            setMotyw(wybrany);

            const oknoWidzianeNaKoncie = p[KLUCZ_OKNA_WYGLADU] === WERSJA_OKNA_WYGLADU;
            setOknoNaKoncie(oknoWidzianeNaKoncie);
            if (oknoWidzianeNaKoncie) zapiszLustroOkna(idKonta);

            // Podpis tego, co JUŻ leży na koncie — zapis niżej rusza tylko przy realnej różnicy (bez echa).
            ostatniZapis.current = JSON.stringify(zestawKonta({
                accent: ACCENT_IDS.includes(p.accent) ? p.accent : null,
                accentByUser: ACCENT_IDS.includes(p.accent),
                font: FONT_IDS.includes(p.font) ? p.font : DEFAULT_FONT,
                motyw: motywNaKoncie,
            }));
            wczytanoZKonta.current = true;
            setPreferencjeWczytane(true);

            // Przeglądarka pamięta zamknięte okno, a serwer nie (zapis nie doszedł) → dosyłamy, nie pokazujemy.
            if (!oknoWidzianeNaKoncie && czytajLustroOkna(idKonta)) wyslijZnacznikOkna();
        })();
        return () => {
            anulowane = true;
            odczytWLocie.current = false;
            if (ponowienie) clearTimeout(ponowienie);
        };
    }, [user?.id, wPodgladzie, hasloDoZmiany, probaOdczytu, wyslijZnacznikOkna]);

    /* [domknięcie po krytyku 2026-10-04] Po nieudanym odczycie nie czekamy bezczynnie na następny termin, gdy przeglądarka
       sama mówi, że warto spróbować: wróciła sieć albo karta wróciła na wierzch (typowo rano, po nocnym wdrożeniu). */
    useEffect(() => {
        if (!user?.id || wPodgladzie || hasloDoZmiany || preferencjeWczytane) return undefined;
        const sprobujTeraz = () => {
            if (odczytWLocie.current || probyOdczytu.current.ile === 0) return;
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            setProbaOdczytu((x) => x + 1);
        };
        window.addEventListener("online", sprobujTeraz);
        document.addEventListener("visibilitychange", sprobujTeraz);
        return () => {
            window.removeEventListener("online", sprobujTeraz);
            document.removeEventListener("visibilitychange", sprobujTeraz);
        };
    }, [user?.id, wPodgladzie, hasloDoZmiany, preferencjeWczytane]);

    /* [poprawka po przeglądzie 2026-10-04] DRUGA KARTA tej samej przeglądarki: wybór motywu zrobiony tam przychodzi tutaj
       (zdarzenie `storage`). Bez tego nieaktualna karta przy następnej zmianie koloru wysyłała zestaw ze STARYM motywem,
       a PUT (podmiana CAŁEGO obiektu) kasował wybór zrobiony przed chwilą w drugiej karcie. W podglądzie pomijamy. */
    /* [sprawdzenie całości 2026-10-04] …ale ta karta NIE ODSYŁA tamtego wyboru echem. Wybór wysłała już karta, w której
       kliknięto, a kolor i czcionka TEJ karty bywają nieaktualne (zmienione w tamtej, bez odświeżenia tutaj). BYŁO ŹLE:
       zmiana motywu z innej karty wyglądała dla efektu zapisu jak różnica i po 600 ms szedł PUT {stary kolor, nowy motyw}
       — podmiana CAŁEGO obiektu kasowała na koncie kolor wybrany w drugiej karcie. Gdy ta karta ma WŁASNĄ zmianę w toku
       (zapis czeka albo się nie udał), echa nie tłumimy: jej zmiana pójdzie razem z nowym motywem. */
    const stanDoZapisu = useRef(null);
    stanDoZapisu.current = { accent, accentByUser, font, motyw };
    useEffect(() => {
        const naZmianeWInnejKarcie = (e) => {
            if (e.key !== KLUCZ_MOTYWU && e.key !== null) return;
            if (podgladRef.current) return;
            const nowy = czytajWyborMotywu();
            const stan = stanDoZapisu.current;
            if (wczytanoZKonta.current && stan && ostatniZapis.current === JSON.stringify(zestawKonta(stan))) {
                ostatniZapis.current = JSON.stringify(zestawKonta({ ...stan, motyw: nowy }));
            }
            setMotyw(nowy);
        };
        window.addEventListener("storage", naZmianeWInnejKarcie);
        return () => window.removeEventListener("storage", naZmianeWInnejKarcie);
    }, []);

    // Domyślna paleta SPÓŁKI zalogowanego — stosowana tylko, gdy pracownik nie wybrał własnej.
    const companyDefault = useMemo(() => {
        const id = user?.companyId != null ? companyAccents?.[String(user.companyId)] : null;
        return id && ACCENT_IDS.includes(id) ? id : null;
    }, [user?.companyId, companyAccents]);

    useEffect(() => {
        if (accentByUser) return;
        // Bez własnego wyboru interfejs podąża za spółką (a bez spółki — za domyślnym).
        setAccentState(companyDefault || DEFAULT_ACCENT);
    }, [accentByUser, companyDefault]);

    useEffect(() => {
        document.documentElement.setAttribute("data-accent", accentEfektywny);
    }, [accentEfektywny]);

    // Źródło koloru dla arkusza brandingu (BrandingContext): przy "user"/"company" kolor marki
    // (themeColor z panelu) przestaje pasować selektorem i rządzi wybrana paleta.
    useEffect(() => {
        const src = accentByUser ? "user" : (companyDefault ? "company" : "default");
        document.documentElement.setAttribute("data-accent-src", src);
    }, [accentByUser, companyDefault]);

    useEffect(() => {
        if (fontEfektywny && fontEfektywny !== "default") document.documentElement.setAttribute("data-font", fontEfektywny);
        else document.documentElement.removeAttribute("data-font");
    }, [fontEfektywny]);

    // Persystencja LOKALNA (pamięć podręczna na czas startu — żeby nie mignął domyślny wygląd).
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ accent, font, accentByUser }));
        } catch { /* quota */ }
    }, [accent, font, accentByUser]);

    /* Persystencja NA KONCIE. Odkładamy o chwilę, bo klikanie po próbnikach kolorów to seria zmian
       stanu — bez tego każdy przejechany kolor byłby osobnym zapisem do bazy. */
    useEffect(() => {
        // Zapis tylko po UDANYM odczycie tego konta (stan i flaga — oba zerowane przy zmianie konta i trybu podglądu).
        if (!user?.id || !preferencjeWczytane || !wczytanoZKonta.current) return;
        // [BC-10] Z włączoną nakładką NIE zapisujemy niczego na konto: na ekranie stoją cudze kolory,
        // a zapis podmieniłby administratorowi jego własny wybór. Wygląd wraca sam po wyłączeniu nakładki.
        if (wPodgladzie) return;
        // [2026-10-03] Motyw jedzie w TYM SAMYM zestawie (PUT podmienia cały obiekt) — tylko gdy wybrany jawnie.
        const doZapisu = zestawKonta({ accent, accentByUser, font, motyw });
        const podpis = JSON.stringify(doZapisu);
        if (ostatniZapis.current === podpis) return;
        const idKonta = user.id;

        const t = setTimeout(() => {
            ostatniZapis.current = podpis;
            wKolejce(() => uiPreferencesApi.zapisz(doZapisu)).then(() => {
                // Serwer przyjął motyw → wybór w tej przeglądarce staje się „zgodny z kontem” (patrz motywAplikacji.js).
                if (doZapisu.theme && czytajWyborMotywu() === doZapisu.theme
                    && czytajWlascicielaMotywu() === wlascicielOczekujacy(idKonta)) {
                    zapiszWlascicielaMotywu(wlascicielZgodny(idKonta));
                }
            }).catch(() => {
                // Nieudany zapis nie może przerwać pracy; przy następnej zmianie spróbujemy ponownie.
                ostatniZapis.current = null;
            });
        }, 600);
        return () => clearTimeout(t);
        // `preferencjeWczytane` w zależnościach: wybór zrobiony przed zalogowaniem, którego konto jeszcze nie ma,
        // nie zmienia stanu przy wczytaniu — bez tej zależności czekałby na następne kliknięcie.
    }, [accent, font, accentByUser, motyw, user?.id, wPodgladzie, preferencjeWczytane, wKolejce]);

    /** [2026-10-03] Jawny wybór motywu (kliknięcie). Przeglądarka od razu, konto zapisem wyżej.
        Zalogowany → „oczekuje na zapis” tego konta; przed zalogowaniem → „kliknięty na logowaniu, teraz”.
        W PODGLĄDZIE cudzego konta zmienia się tylko ekran: pamięć przeglądarki należy do administratora
        (poprawka po przeglądzie 2026-10-04), a po wyjściu z podglądu wraca jego własny motyw. */
    const ustawMotyw = useCallback((nowy) => {
        if (!czyMotyw(nowy)) return;
        if (!podgladRef.current) {
            zapiszWyborMotywu(nowy, user?.id != null ? wlascicielOczekujacy(user.id) : wlascicielLogowania());
        }
        setMotyw(nowy);
    }, [user?.id]);

    /** [2026-10-03] Okno „Wygląd aplikacji” zamknięte (✕, Escape, „Rozumiem”, „Przejdź do ustawień wyglądu”).
        Lustro od razu, znacznik na koncie w kolejce zapisów. W podglądzie konta nic nie idzie na serwer ani do
        pamięci przeglądarki (bramka tylko-do-odczytu i tak by odrzuciła, a znacznik należałby do oglądanej osoby). */
    const oznaczOknoWygladu = useCallback(() => {
        setOknoLokalnie(true);
        if (user?.id == null || wPodgladzie) return Promise.resolve();
        zapiszLustroOkna(user.id);
        return wyslijZnacznikOkna();
    }, [user?.id, wPodgladzie, wyslijZnacznikOkna]);

    const setAccent = useCallback((a) => {
        if (!ACCENT_IDS.includes(a)) return;
        setAccentState(a);
        setAccentByUser(true); // klik człowieka = od teraz jego wybór, spółka już nie nadpisuje
    }, []);
    const setFont = useCallback((f) => { if (FONT_IDS.includes(f)) setFontState(f); }, []);
    const resetPersonalization = useCallback(() => {
        // Reset = powrót do ustawienia spółki (albo domyślnego), nie na sztywno do "granat".
        setAccentByUser(false);
        setAccentState(companyDefault || DEFAULT_ACCENT);
        setFontState(DEFAULT_FONT);
        // [2026-10-03] „Przywróć domyślne” obejmuje też motyw: domyślny = jasny, bez zapisanego wyboru.
        // W podglądzie cudzego konta pamięć przeglądarki należy do administratora — zmienia się tylko ekran.
        if (!podgladRef.current) zapiszWyborMotywu(null);
        setMotyw(null);
    }, [companyDefault]);

    const oknoWygladuWidziane = oknoNaKoncie || oknoLokalnie;

    const value = useMemo(() => ({
        accent, font,
        accents: ACCENTS, fonts: FONTS,
        setAccent, setFont, resetPersonalization,
        accentByUser,
        companyDefault,
        isDefault: !accentByUser && font === DEFAULT_FONT && (motyw == null || motyw === MOTYW_DOMYSLNY),
        // [2026-10-03] motyw na koncie + okno „Wygląd aplikacji”
        motyw,
        ustawMotyw,
        preferencjeWczytane,
        oknoWygladuWidziane,
        oznaczOknoWygladu,
    }), [accent, font, setAccent, setFont, resetPersonalization, accentByUser, companyDefault,
        motyw, ustawMotyw, preferencjeWczytane, oknoWygladuWidziane, oznaczOknoWygladu]);

    const kanalMotywu = useMemo(() => ({ motyw, ustawMotyw }), [motyw, ustawMotyw]);

    return (
        <PersonalizationContext.Provider value={value}>
            <KanalMotywuKonta.Provider value={kanalMotywu}>
                {children}
            </KanalMotywuKonta.Provider>
        </PersonalizationContext.Provider>
    );
};

export default PersonalizationContext;
